import type { StoreNotificationSummary } from "@/types/storeNotifications";
import { create } from "zustand";

/**
 * Online orders for the current store: whether it ships them, how many it
 * still has to ship, and the notifications that announce new ones.
 *
 * Shared because two features read it: the scheduler toolbar draws the badge,
 * and the Encomendas Online page asks for a fresh count after marking them seen.
 * `useOnlineOrdersWatcher` is the only writer of the summary and the count.
 */
type OnlineOrdersState = {
  /**
   * The service's answer, with the store it answers for, so it can never be
   * read as another store's. `null` until the service has answered. Read it
   * through `selectEnabledFor`.
   */
  enabledFor: { storeId: number; enabled: boolean } | null;
  /** Notifications nobody at the store has seen yet. */
  unseenCount: number;
  /** Orders the store still has to ship: what the toolbar badge shows. */
  toFulfilCount: number;
  /** Whether a summary has come in: the first only records what is there. */
  summarised: boolean;
  /**
   * The newest notification any summary has shown. Kept after it is seen, so
   * an older one still unseen behind it is never announced as new.
   */
  newestNotificationId: number | null;
  /** Bumped to ask the watcher to poll now instead of at the next tick. */
  refreshToken: number;
  /** Bumped whenever a poll finds a notification newer than any before. */
  arrivalToken: number;
};

type OnlineOrdersActions = {
  setEnabled: (enabled: boolean, storeId: number) => void;
  /** Records a summary and answers whether a new notification arrived. */
  applySummary: (summary: StoreNotificationSummary) => boolean;
  setToFulfilCount: (count: number) => void;
  requestRefresh: () => void;
  reset: () => void;
};

const initialState: OnlineOrdersState = {
  enabledFor: null,
  unseenCount: 0,
  toFulfilCount: 0,
  summarised: false,
  newestNotificationId: null,
  refreshToken: 0,
  arrivalToken: 0,
};

export const useOnlineOrdersStore = create<
  OnlineOrdersState & OnlineOrdersActions
>((set, get) => ({
  ...initialState,
  setEnabled: (enabled, storeId) => set({ enabledFor: { storeId, enabled } }),
  applySummary: ({ unseenCount, latest }) => {
    const { summarised, newestNotificationId } = get();
    /* Ids only grow, so a higher one was created since. */
    const newer =
      latest !== null &&
      (newestNotificationId === null || latest.id > newestNotificationId);
    const arrived = summarised && newer;

    set(state => ({
      unseenCount,
      summarised: true,
      newestNotificationId: newer ? latest.id : newestNotificationId,
      arrivalToken: arrived ? state.arrivalToken + 1 : state.arrivalToken,
    }));

    return arrived;
  },
  setToFulfilCount: toFulfilCount => set({ toFulfilCount }),
  requestRefresh: () =>
    set(state => ({ refreshToken: state.refreshToken + 1 })),
  reset: () => set(initialState),
}));

/**
 * Whether `storeId` ships online orders: `null` until the answer is for that
 * store, so the previous store's answer never shows during a store switch.
 */
export const selectEnabledFor =
  (storeId: number | null) =>
  (state: OnlineOrdersState): boolean | null =>
    storeId !== null && state.enabledFor?.storeId === storeId
      ? state.enabledFor.enabled
      : null;
